import {
  readPortalPreviewSearch,
  type PortalBrandingResponse,
  type PortalPreviewBrand,
  type PortalPreviewScreen,
} from "@ledgerbox/domain"

import { findDemoProfile, type DemoProfile } from "./profiles"

/**
 * Where a demo comes from.
 *
 * The path decides the persona, and the query decides the brand. Demo mode is
 * keyed on the `/demo/` path rather than on a hostname, because the same build
 * serves it twice: publicly at demo.<portal domain>, and inside the app, where
 * the portal studio frames the firm's own portal host so the preview and the
 * real thing are one origin.
 *
 * The query is read with `@ledgerbox/domain`'s preview module, the same one
 * the app writes it with. After the first load the app sends messages
 * instead (see ./preview-channel.ts).
 */

const HEX = /^#[0-9a-fA-F]{6}$/

/** Which of the portal's screens the in-app preview is showing. */
export type DemoScreen = PortalPreviewScreen

export type DemoRoute = {
  profile: DemoProfile
  screen: DemoScreen
  /**
   * Set when the caller already knows the brand: the in-app preview passes the
   * firm's unsaved draft in the query. A demo with a brand needs no email,
   * because there is nothing left to buy with it.
   */
  preset: PortalBrandingResponse | null
  /**
   * The firm app that framed this preview, and the only origin whose
   * messages it takes. Null on the public demo, which takes none.
   */
  parentOrigin: string | null
  /** The firm's accounting access details, for the Request item page. */
  access: DemoAccess
}

/** What an accounting access item shows of the firm. */
export type DemoAccess = Pick<
  PortalPreviewBrand,
  "accountantEmail" | "quickbooksFirmId"
>

export const accessFromPreview = (
  brand: Partial<PortalPreviewBrand>
): DemoAccess => ({
  accountantEmail: brand.accountantEmail ?? "",
  quickbooksFirmId: brand.quickbooksFirmId ?? "",
})

export const buildDemoBranding = (input: {
  displayName: string
  accentColor: string
  logoUrl: string | null
  showNameWithLogo?: boolean
  homeGreeting?: string | null
  homeMessage?: string | null
  supportName?: string | null
  supportEmail?: string | null
  supportPhone?: string | null
}): PortalBrandingResponse => ({
  slug: "demo",
  displayName: input.displayName,
  accentColor: HEX.test(input.accentColor) ? input.accentColor : "#2f6f4e",
  logoUrl: input.logoUrl,
  poweredBy: "LedgerBox",
  supportName: input.supportName ?? null,
  supportEmail: input.supportEmail ?? null,
  supportPhone: input.supportPhone ?? null,
  showNameWithLogo: input.showNameWithLogo ?? true,
  homeGreeting: input.homeGreeting ?? null,
  homeMessage: input.homeMessage ?? null,
})

/**
 * A preview brand as the portal's own branding. What the brand leaves out
 * comes from the persona, and empty copy means the portal's own words. The
 * query at load and every message after it go through here, so the first
 * frame and a live one cannot disagree.
 */
export const brandingFromPreview = (
  brand: Partial<PortalPreviewBrand>,
  profile: DemoProfile
): PortalBrandingResponse =>
  buildDemoBranding({
    displayName: brand.displayName || profile.fallbackFirmName,
    accentColor: brand.accentColor ?? profile.fallbackAccent,
    logoUrl: brand.logoUrl ?? null,
    showNameWithLogo: brand.showNameWithLogo ?? true,
    homeGreeting: brand.homeGreeting || null,
    homeMessage: brand.homeMessage || null,
    supportName: brand.supportName || null,
    supportEmail: brand.supportEmail || null,
    supportPhone: brand.supportPhone || null,
  })

/** The page sits in a frame, as the firm app's live preview does. */
const inFrame = (): boolean =>
  typeof window !== "undefined" && window.self !== window.top

export const readDemoRoute = (
  location: Pick<Location, "pathname" | "search"> = window.location,
  framed: boolean = inFrame()
): DemoRoute | null => {
  const match = location.pathname.match(/^\/demo(?:\/([a-z0-9-]+))?\/?$/)
  if (!match) return null
  const profile = findDemoProfile(match[1] ?? null)
  const query = readPortalPreviewSearch(new URLSearchParams(location.search))
  const { brand } = query
  // Branding from the query is for the firm app's preview frame only. Opened
  // as a page on a real firm's host, a crafted link could otherwise show that
  // firm's name beside someone else's message and contact details.
  const branded =
    framed &&
    Boolean(brand.displayName || brand.accentColor || brand.logoUrl)
  const parentOrigin = branded ? query.parentOrigin : null
  return {
    profile,
    preset: branded ? brandingFromPreview(brand, profile) : null,
    // Only a preview listens. The public demo has no firm app behind it.
    parentOrigin,
    // The access details only come by message from the firm app, so the
    // Request item page opens empty until the app sends them, and a link
    // without a firm app behind it never shows that page.
    screen: query.screen === "item" && !parentOrigin ? "home" : query.screen,
    access: accessFromPreview({}),
  }
}
